<template>
    <transition name="fade">
        <div v-if="show" class="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/45 px-6 backdrop-blur-[2px]">
            <div class="w-full max-w-2xl overflow-hidden rounded-[32px] border border-cyan-100/60 bg-white shadow-2xl">
                <div class="bg-[radial-gradient(circle_at_top_left,_rgba(236,254,255,0.95),_rgba(248,250,252,0.98)_45%,_rgba(255,255,255,1))] px-8 py-7">
                    <div class="flex flex-col gap-6 md:flex-row md:items-start">
                        <div class="flex h-16 w-16 shrink-0 items-center justify-center rounded-[24px] bg-white shadow-sm ring-1 ring-cyan-100">
                            <div class="relative h-9 w-9">
                                <div class="absolute inset-0 rounded-full border-4 border-cyan-100"></div>
                                <div class="absolute inset-0 animate-spin rounded-full border-4 border-transparent border-t-cyan-500 border-r-cyan-300"></div>
                            </div>
                        </div>

                        <div class="min-w-0 flex-1">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-600">Inteligência Clínica</div>
                            <h2 class="mt-2 text-2xl font-bold text-slate-800">Gerando conclusão profissional</h2>
                            <p class="mt-2 text-sm leading-6 text-slate-500">
                                A IA está cruzando achados da avaliação, matriz de risco e contexto clínico para montar uma conclusão técnica e editável.
                            </p>

                            <div class="mt-6 h-36 overflow-hidden rounded-[24px] border border-slate-100 bg-slate-950 px-5 py-5 text-sm text-slate-300 shadow-inner">
                                <div class="font-mono leading-7">
                                    {{ typedPhrase }}
                                    <span class="ml-0.5 inline-block h-5 w-[2px] animate-pulse bg-cyan-300 align-middle"></span>
                                </div>
                            </div>

                            <div class="mt-5 grid gap-3 sm:grid-cols-3">
                                <div class="rounded-2xl border border-slate-100 bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                                    Análise biomecânica
                                </div>
                                <div class="rounded-2xl border border-slate-100 bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                                    Correlação clínica
                                </div>
                                <div class="rounded-2xl border border-slate-100 bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                                    Priorização terapêutica
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import { ref, watch, onBeforeUnmount } from 'vue';

const PHRASES = [
    'Analisando dados antropométricos, testes funcionais e histórico clínico do paciente...',
    'Correlacionando assimetrias bilaterais com matriz de risco e demandas da modalidade esportiva...',
    'Calculando relevância clínica de déficits de força, mobilidade e controle neuromuscular...',
    'Buscando cruzamentos entre queixa principal, HDA, fatores psicossociais e achados objetivos...',
    'Estruturando conclusão técnico-científica com prioridades terapêuticas e linguagem profissional...'
];

export default {
    name: 'ConclusionGenerationOverlay',
    props: {
        show: {
            type: Boolean,
            default: false
        }
    },
    setup(props) {
        const typedPhrase = ref('');
        const phraseIndex = ref(0);

        let typingTimer = null;
        let holdTimer = null;

        function clearTimers() {
            if (typingTimer) {
                clearTimeout(typingTimer);
                typingTimer = null;
            }

            if (holdTimer) {
                clearTimeout(holdTimer);
                holdTimer = null;
            }
        }

        function typePhrase() {
            clearTimers();

            const phrase = PHRASES[phraseIndex.value % PHRASES.length];
            typedPhrase.value = '';

            const step = (charIndex = 0) => {
                typedPhrase.value = phrase.slice(0, charIndex);

                if (charIndex <= phrase.length) {
                    typingTimer = setTimeout(() => step(charIndex + 1), 24);
                    return;
                }

                holdTimer = setTimeout(() => {
                    phraseIndex.value = (phraseIndex.value + 1) % PHRASES.length;
                    typePhrase();
                }, 1300);
            };

            step(1);
        }

        watch(() => props.show, (visible) => {
            if (visible) {
                phraseIndex.value = 0;
                typePhrase();
                return;
            }

            clearTimers();
            typedPhrase.value = '';
        }, { immediate: true });

        onBeforeUnmount(() => {
            clearTimers();
        });

        return {
            typedPhrase
        };
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
